/**
 * DeleteConfirmationModal - Confirmation dialog for notebook deletion
 *
 * @module components/modals/DeleteConfirmationModal
 */

import React, { useEffect } from 'react';

import { Trash2 } from 'lucide-react';

/**
 * DeleteConfirmationModal Component
 *
 * @param {Object} props
 * @param {string} props.notebookName - Name of notebook to delete
 * @param {Function} props.onConfirm - Confirm deletion
 * @param {Function} props.onCancel - Cancel deletion
 */
const DeleteConfirmationModal = ({ notebookName, onConfirm, onCancel }) => {
  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onConfirm, onCancel]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Delete Notebook
            </h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-600 dark:text-neutral-300">
            Are you sure you want to delete{' '}
            <span className="font-medium text-slate-900 dark:text-white">"{notebookName}"</span>?
          </p>
          <p className="mt-2 text-sm text-slate-500 dark:text-neutral-400">
            This action cannot be undone. All files and chat history in this notebook will be
            permanently deleted.
          </p>
        </div>

        {/* Actions */}
        <div className="p-6 pt-0 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
